// Top-level compiler API: the single surface the CLI, tests, and playground use.

import * as ast from "./frontend/ast.js";
import { analyze, check, Diagnostic } from "./frontend/checker.js";
import { Interpreter, RunOptions, RunResult } from "./backend/interp/interpreter.js";
import { LexError } from "./frontend/lexer.js";
import { parse as parseSource, parseRecover, ParseError } from "./frontend/parser.js";
import { LockPlan, PlanOptions, TxnPlanEntry, planLocks, txnPlanEntries } from "./middle-end/lockplan.js";
import { monomorphize } from "./middle-end/monomorphize.js";
import { eliminateDeadCode } from "./middle-end/dce.js";
import { dumpCore } from "./middle-end/core-dump.js";
import { generateGo } from "./backend/go/gogen.js";
import { Span } from "./frontend/token.js";

export type { Diagnostic, DefInfo, SemanticIndex } from "./frontend/checker.js";
export type { Type } from "./frontend/types.js";
export type { RunOptions, RunResult } from "./backend/interp/interpreter.js";
export type { Program } from "./frontend/ast.js";
export type {
  Completion,
  Hover,
  DocumentSymbol,
  SymbolKind,
  SignatureHelp,
  SignatureInfo,
  SignatureParam,
} from "./frontend/language-service.js";
export type { LockPlan, MemoryMode, PlanOptions, TxnPlanEntry, TxnStrategy } from "./middle-end/lockplan.js";
export { analyze, check } from "./frontend/checker.js";
export { LanguageService, analyzeSource } from "./frontend/language-service.js";
export { STDLIB_FILES } from "./frontend/stdlib-src.js";
export type { StdlibFile } from "./frontend/stdlib-src.js";
export { planLocks, lockPlanToJSON, txnPlanEntries } from "./middle-end/lockplan.js";

export interface ParseResult {
  program: ast.Program | null;
  diagnostics: Diagnostic[];
}

/** Parse source, converting lex/parse failures into diagnostics. */
export function parse(src: string): ParseResult {
  try {
    return { program: parseSource(src), diagnostics: [] };
  } catch (e) {
    if (e instanceof ParseError || e instanceof LexError) {
      return { program: null, diagnostics: [{ severity: "error", message: e.message, span: e.span }] };
    }
    throw e;
  }
}

/** Parse + type-check, returning diagnostics for the user's document. A finding
 *  located in a bundled stdlib file (a `source`-tagged span) is dropped: the
 *  prelude is assumed well-formed, and its offsets index a different file, so it
 *  must never be rendered against the user's buffer (mirrors
 *  `LanguageService.diagnostics`). */
export function checkSource(src: string): Diagnostic[] {
  const { program, diagnostics } = parse(src);
  if (!program) return diagnostics;
  return check(program).filter((d) => !d.span.source);
}

export interface PlanResult {
  plan: LockPlan;
  /** Per-`txn` decisions, located in source and in source order. */
  entries: TxnPlanEntry[];
}

/**
 * Parse (recovering from syntax errors) + run the concurrency-control analysis,
 * for the playground's lock-plan panel and inline editor annotations. Mirrors
 * `analyzeSource`: never throws, and works on an in-progress edit because the
 * parser keeps whatever declarations are well-formed. Returns null only when
 * nothing at all parses.
 */
export function planSource(src: string, opts: PlanOptions = {}): PlanResult | null {
  const { program } = parseRecover(src);
  if (program.decls.length === 0) return null;
  // Hybrid planning needs types; `analyze` is lenient, so it works on a recovered
  // (in-progress) parse the same way the language service does.
  const elaboration = opts.elaboration ?? analyze(program).elaboration;
  const plan = planLocks(program, { ...opts, elaboration });
  return { plan, entries: txnPlanEntries(plan) };
}

/** Parse, then run the program's `main`. Throws on parse errors. */
export function run(src: string, opts: RunOptions = {}): RunResult {
  const { program, diagnostics } = parse(src);
  if (!program) {
    const d = diagnostics[0];
    throw new CompileError(d ? d.message : "parse error", d?.span);
  }
  // Type-check first: like `stm go`, a program with type errors doesn't run. The
  // analysis also produces the Elaboration — the per-node IR the interpreter reads
  // to classify operations (iteration, dispatch, operators) instead of re-deriving
  // them, so it draws the same conclusions the checker and the Go backend do.
  const index = analyze(program);
  const error = index.diagnostics.find((d) => d.severity === "error");
  if (error) throw new CompileError(error.message, error.span);
  return new Interpreter(program, opts, index.elaboration).run();
}

/** The result of lowering source to a textual IR/backend output. Exactly one of
 *  `code` / `error` is set: `code` holds the rendered output on success, `error`
 *  a human-readable message when the program doesn't parse, doesn't type-check, or
 *  uses a construct the pass can't lower yet (e.g. tables in the Go backend). Both
 *  the Core and Go views need this same "show it or explain why not" shape — it
 *  backs the playground's godbolt-style inspection tabs. */
export interface EmitResult {
  code: string | null;
  error: string | null;
}

/** Options shared by the compiled-output views: which concurrency-control strategy
 *  to plan (baked into Core and read off it by the Go printer). */
export interface EmitOptions {
  strategy?: "occ" | "hybrid";
}

/** Parse + type-check + monomorphize + dead-code elimination, returning a readable
 *  dump of the Core IR — dispatch resolved, generics/traits specialized per concrete
 *  type, and everything `main` can't reach dropped (so the dump matches what the Go
 *  backend lowers). Never throws: parse/type errors and not-yet-lowerable constructs
 *  come back as `error`. */
export function emitCore(src: string, opts: EmitOptions = {}): EmitResult {
  return lower(src, opts, (program, elaboration, plan) =>
    dumpCore(eliminateDeadCode(monomorphize(program, plan, elaboration)))
  );
}

/** Parse + type-check + lower all the way to Go source. Same never-throws contract
 *  as `emitCore`: a construct the Go backend can't emit yet (tables) comes back as
 *  `error` rather than throwing. */
export function emitGo(src: string, opts: EmitOptions = {}): EmitResult {
  return lower(src, opts, (program, elaboration) =>
    generateGo(program, { strategy: opts.strategy ?? "occ" }, elaboration)
  );
}

/** Shared front half of `emitCore` / `emitGo`: parse, type-check, and plan locks,
 *  then hand the caller the pieces to render. Any diagnostic error short-circuits
 *  to an `error` result, and a throw from the lowering step (an unlowerable
 *  construct) is caught and surfaced the same way. */
function lower(
  src: string,
  opts: EmitOptions,
  render: (program: ast.Program, elaboration: import("./middle-end/elaboration.js").Elaboration, plan: LockPlan) => string
): EmitResult {
  const { program, diagnostics } = parse(src);
  if (!program) return { code: null, error: diagnostics[0]?.message ?? "parse error" };
  const index = analyze(program);
  const error = index.diagnostics.find((d) => d.severity === "error");
  if (error) return { code: null, error: error.message };
  const strategy = opts.strategy ?? "occ";
  try {
    const plan = planLocks(program, { strategy, elaboration: index.elaboration });
    return { code: render(program, index.elaboration, plan), error: null };
  } catch (e) {
    return { code: null, error: e instanceof Error ? e.message : String(e) };
  }
}

export class CompileError extends Error {
  constructor(message: string, public span?: Span) {
    super(message);
    this.name = "CompileError";
  }
}
